import { useMemo } from "react";

import { useGmnListAllPagesQuery } from "@/queries/query-gmn";
import { useGmwGeojsonQuery } from "@/queries/query-gmw";
import { useOrganisationsListAllPagesQuery } from "@/queries/query-organisations";

export type GMNFilters = { gmnIds?: string[]; unlinkedChecked?: boolean };

export type GMNFilterProps = {
  filters: GMNFilters;
  setFilters: (filters: Partial<GMNFilters>) => void;
};

const useGmnCheckboxInfo = ({ filters, setFilters }: GMNFilterProps) => {
  const { data: gmwGeojsonData } = useGmwGeojsonQuery();
  const { data: gmnData } = useGmnListAllPagesQuery();
  const gmns = useMemo(() => {
    if (!gmnData || !gmwGeojsonData) return [];
    return gmnData.results.filter((gmn) =>
      gmwGeojsonData.features.some((feature) =>
        feature.properties.linkedGmns?.includes(gmn.uuid + "")
      )
    );
  }, [gmnData, gmwGeojsonData]);
  const { data: orgData } = useOrganisationsListAllPagesQuery();
  const organisations = useMemo(() => orgData?.results ?? [], [orgData]);

  const filteredGmnIds = filters.gmnIds;
  const unlinkedChecked = filters.unlinkedChecked ?? true;

  const checkboxOptions = useMemo(() => {
    // Map the organisations to an object to quickly get them in the gmn map
    const organisationNameMap: { [kvk: string]: string } = {};
    organisations.forEach(({ kvkNumber, name }) => (organisationNameMap[kvkNumber + ""] = name));
    return gmns.map((gmn) => ({
      label: gmn.name + "",
      uuid: gmn.uuid + "",
      checked: !filteredGmnIds || !!filteredGmnIds.find((gmnId) => gmnId === gmn.uuid),
      color: gmn.color || undefined,
      category: organisationNameMap[gmn.deliveryAccountableParty + ""] || "Onbekend",
    }));
  }, [organisations, gmns, filteredGmnIds]);

  const gmnIdsFilterFunction = (gmnIds: string[]) => {
    setFilters({ gmnIds });
  };

  const handleUnlinkedClick = (unlinkedChecked: boolean) => {
    setFilters({ unlinkedChecked });
  };

  const checkedArray = checkboxOptions.filter(({ checked }) => checked);
  const allChecked =
    checkedArray.length === checkboxOptions.length &&
    (unlinkedChecked === undefined || unlinkedChecked);

  return {
    unlinkedChecked,
    checkboxOptions,
    gmnIdsFilterFunction,
    handleUnlinkedClick,
    checkedArray,
    allChecked,
  };
};

export { useGmnCheckboxInfo };
